import React from 'react';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';

import { makeStyles } from '@mui/styles';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';

import { CustomDataTableConfig, HeadCell, Order } from './custom-data-table-config.model';
import { ArrayUtils } from '../../utils/array.utils';
import { Chip, lighten } from '@mui/material';
import { ObjectUtils } from '../../utils/object.utils';
import Container from '@mui/material/Container';
import { customTheme } from '../../../styles/theme';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis?.map(el => el[0]);
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headCells: HeadCell[];
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow style={{ height: 53 }}>
        <TableCell padding={'normal'} align={'left'}>
          Nr.
        </TableCell>

        {props.headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={'left'}
            padding={'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles({
  root: {
    paddingLeft: customTheme.spacing(2),
    paddingRight: customTheme.spacing(1),
  },
  highlight:
    customTheme.palette.mode === 'light'
      ? {
          color: customTheme.palette.secondary.main,
          backgroundColor: lighten(customTheme.palette.secondary.light, 0.85),
        }
      : {
          color: customTheme.palette.text.primary,
          backgroundColor: customTheme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
});

interface EnhancedTableToolbarProps {
  numSelected: number;
  tableName?: string;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <Container style={{ width: '100%', maxWidth: '100%' }}>
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          {props.tableName || ''}
        </Typography>

        {numSelected > 0 && (
          <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
            {numSelected} selected
          </Typography>
        )}
      </Container>
    </Toolbar>
  );
};

const useStyles = makeStyles({
  paper: {
    width: '100%',
    marginBottom: customTheme.spacing(2),
  },
  table: {
    minWidth: 550,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  marginRight: {
    marginRight: customTheme.spacing(1),
  },
});

export default function CustomDataTableComponent(props: {
  tableConfig: CustomDataTableConfig;
  data: any[];
  onSelect?: Function;
}) {
  let config = props.tableConfig;
  let data = props.data;

  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<any>(config.defaultOrderBy);
  const [selected, setSelected] = React.useState<number[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(config.rowsPerPage || 5);

  // use this syntax to watch change of props
  React.useEffect(() => {
    setPage(0);
  }, [props]);

  const getRowColor = (item: any): string => {
    if (!props.tableConfig?.highlightRowColorFunc) {
      return 'unset';
    }

    return props.tableConfig.highlightRowColorFunc(item) || 'unset';
  };

  const getCellValue = (row: any, headCell: HeadCell) => {
    if (headCell.field) {
      return ObjectUtils.getValueOfField(row, headCell.field);
    }

    return headCell.getFieldValueFunc(row);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = data?.map(n => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, item: any) => {
    props.onSelect(item);
  };

  const handleChangePage = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  return (
    <Paper className={classes.paper}>
      <EnhancedTableToolbar numSelected={selected.length} tableName={config.tableName} />
      <TableContainer>
        <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
          <EnhancedTableHead
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={data?.length}
            headCells={config.headCells}
          />
          <TableBody>
            {stableSort(data, getComparator(order, orderBy))
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((row, index) => {
                const isItemSelected = isSelected(row.id as number);

                return (
                  <TableRow
                    hover
                    onClick={event => handleClick(event, row)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    style={{ height: 53, backgroundColor: getRowColor(row) }}
                  >
                    <TableCell padding="normal">{page * rowsPerPage + index + 1}</TableCell>

                    {config.headCells?.map(headCell => {
                      return (
                        <TableCell padding="normal" key={row.id + headCell.id}>
                          {ArrayUtils.isArray(getCellValue(row, headCell))
                            ? (getCellValue(row, headCell) as unknown as any[])?.map((item: any) => (
                                <Chip className={classes.marginRight} size="small" key={item} label={item} />
                              ))
                            : getCellValue(row, headCell)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {
        // @ts-ignore
        <TablePagination
          rowsPerPageOptions={[10, 50, 100]}
          component={'span' as React.ElementType}
          count={data?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        ></TablePagination>
      }
    </Paper>
  );
}
